import { DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Flex, Modal, Row, Space } from "antd";
import { useForm } from "antd/es/form/Form";
import FormItem from "antd/es/form/FormItem";
import TableAntd from "components/elements/Table";
import { FormInput, FormSelect } from "components/form/FormProvide";
import FormAntd from "components/form/FormProvide/FormAntd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const WelfareAddPositionModal = ({
    isPositionModalOpen = false,
    setIsPositionModalOpen = () => { },
    positionOptions = [],
    selectedPositions = [],
    onSave = () => { },
    onAddPositionIds = () => { },
    selectedEmployees = [],
    employeeByPositionOptions = [],
    initialEmployeeIgnoreList = [],
}) => {
    const { t, i18n } = useTranslation();
    const [form] = useForm();
    const [tempPositions, setTempPositions] = useState([]);
    const [searchTxt, setSearchTxt] = useState("");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    useEffect(() => {
        if (isPositionModalOpen) {
            setTempPositions(selectedPositions);
            setPagination((prev) => ({ ...prev, total: selectedPositions.length }));

            if (initialEmployeeIgnoreList && initialEmployeeIgnoreList.length > 0) {
                form.setFieldsValue({
                    ignoreEmployees: initialEmployeeIgnoreList,
                });
            } else {
                form.setFieldsValue({
                    ignoreEmployees: [],
                });
            }
        }
    }, [isPositionModalOpen, selectedPositions, initialEmployeeIgnoreList, form]);

    const onAddPosition = (positionIds) => {
        const newPositions = positionOptions
            ?.filter(
                (pos) =>
                    positionIds.includes(pos.value) &&
                    !tempPositions.some((tp) => tp.id === pos.value)
            )
            .map((pos) => ({
                id: pos.value,
                name: pos.name || pos.label,
                nameEn: pos.nameEn || pos.label,
            }));

        const updated = [...tempPositions, ...newPositions];
        setTempPositions(updated);
        setPagination((prev) => ({ ...prev, total: updated.length }));
        form.setFieldValue("positionList", []);

        if (positionIds?.length > 0) {
            const employeeIds = selectedEmployees?.map((e) => e.id) || [];
            onAddPositionIds(positionIds, employeeIds);
        }
    };

    const onRemovePosition = (record) => {
        const updated = tempPositions.filter((pos) => pos.id !== record.id);
        setTempPositions(updated);
        setPagination((prev) => ({ ...prev, total: updated.length }));
    };

    const onFilterPositionBySearch = tempPositions.filter((pos) => {
        if (!searchTxt) return true;
        const searchLower = searchTxt.toLowerCase();
        return (
            pos.name?.toLowerCase().includes(searchLower) ||
            pos.nameEn?.toLowerCase().includes(searchLower)
        );
    });

    const onPagePositionChange = (page, pageSize) => {
        setPagination((prev) => ({ ...prev, current: page, pageSize }));
    };

    const handleSave = () => {
        const ignoreEmployees = form.getFieldValue("ignoreEmployees") || [];
        onSave(tempPositions, ignoreEmployees);
        setSearchTxt("");
    };

    const handleCancel = () => {
        setIsPositionModalOpen(false);
        setTempPositions(selectedPositions);
        form.resetFields();
        setSearchTxt("");
    };

    const columns = [
        {
            title: <div className="text-table" style={{ textAlign: "center" }}>{t("common.index")}</div>,
            dataIndex: "index",
            key: "index",
            render: (_, record, idx) => {
                return (
                    <span
                        style={{
                            color: "#42526D",
                            backgroundColor: "#F5F6F7",
                            borderRadius: "50%",
                            display: "inline-flex",
                            justifyContent: "center",
                            alignItems: "center",
                            margin: "auto",
                            padding: "8px",
                            minWidth: "30px",
                            height: "30px",
                        }}
                    >
                        {((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 10) + idx + 1}
                    </span>
                );
            },
            width: 90,
            align: "center",
        },
        {
            title: <div className="text-table">{t("welfare.label.position_name")}</div>,
            dataIndex: "positionName",
            key: "positionName",
            render: (_, record) => {
                if (i18n.language === "en") {
                    return record?.nameEn || record?.positionNameEn || "-";
                }
                return record?.name || record?.positionName || "-";
            },
        },
        {
            title: "",
            key: "action",
            align: "center",
            width: 90,
            render: (_, record) => (
                <Space size="middle">
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: "1",
                                    onClick: () => onRemovePosition(record),
                                    label: `${t("action.delete")}`,
                                    icon: <DeleteOutlined style={{ fontSize: "18px", color: "gray" }} />,
                                },
                            ],
                        }}
                    >
                        <MoreOutlined style={{ fontSize: "25px", color: "gray" }} />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    return (
        <Modal open={isPositionModalOpen} closable={false} maskClosable={false} footer={null} width={620}>
            <Space size={24} direction="vertical" style={{ width: "100%" }}>
                <Flex vertical>
                    <h3>{t("welfare.label.add_position")}</h3>
                    <span style={{ color: "#667085" }}>{t("welfare.label.add_position_desc")}</span>
                </Flex>

                <FormAntd form={form} onFinish={handleSave} initialValues={initialEmployeeIgnoreList}>
                    <FormItem shouldUpdate>
                        {({ getFieldValue }) => {
                            return (
                                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                                    <Row>
                                        <Col span={18}>
                                            <FormSelect
                                                name="positionList"
                                                label={t("welfare.label.add_position")}
                                                placeholder={t("common.pleaseSelect")}
                                                size="large"
                                                options={positionOptions}
                                                tree={true}
                                                multiple={true}
                                            />
                                        </Col>
                                        <Col span={6} style={{ alignContent: "end", textAlignLast: "end" }}>
                                            <Button
                                                onClick={() => onAddPosition(getFieldValue("positionList"))}
                                                disabled={getFieldValue("positionList") == null || getFieldValue("positionList")?.length == 0}
                                                size="large"
                                                type="primary"
                                                style={{
                                                    borderRadius: "8px",
                                                    backgroundColor:
                                                        getFieldValue("positionList") == null || getFieldValue("positionList")?.length == 0
                                                            ? ""
                                                            : "var(--button-color)",
                                                }}
                                            >
                                                {t("welfare.label.add_position")}
                                            </Button>
                                        </Col>
                                    </Row>

                                    <FormSelect
                                        name="ignoreEmployees"
                                        label={t("welfare.label.except_for")}
                                        placeholder={t("common.pleaseSelect")}
                                        size="large"
                                        mode="multiple"
                                        options={employeeByPositionOptions}
                                    />

                                    <Row style={{ gap: "6px" }}>
                                        <Col span={18}>
                                            <FormInput
                                                name="searchTxt"
                                                label={t("common.search_position")}
                                                placeholder={t("common.fill_in")}
                                                size="large"
                                                allowClear={true}
                                            />
                                        </Col>
                                        <Col span={5} style={{ alignContent: "end" }}>
                                            <Button
                                                type="default"
                                                style={{ borderRadius: "8px", height: "40px", color: "#565656", fontWeight: "bold" }}
                                                onClick={() => setSearchTxt(getFieldValue("searchTxt") || "")}
                                            >
                                                {t("common.search")}
                                            </Button>
                                        </Col>
                                    </Row>

                                    <TableAntd
                                        columns={columns}
                                        dataSource={onFilterPositionBySearch}
                                        rowKey={(record) => record?.id}
                                        noPage={true}
                                        onChange={onPagePositionChange}
                                        pagination={{
                                            ...pagination,
                                            showTotal: (total, range) =>
                                                t("paginate.description")
                                                    .replace("{min}", range[0])
                                                    .replace("{max}", range[1])
                                                    .replace("{total}", total),
                                        }}
                                    />

                                    <Flex gap={8} justify="end">
                                        <Button
                                            type="default"
                                            style={{ borderRadius: "8px", height: "40px", color: "#565656", fontWeight: "bold" }}
                                            onClick={handleCancel}
                                        >
                                            {t("common.cancel")}
                                        </Button>

                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            style={{
                                                borderRadius: "8px",
                                                height: "40px",
                                                backgroundColor: "var(--button-color)",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {t("common.save")}
                                        </Button>
                                    </Flex>
                                </Space>
                            );
                        }}
                    </FormItem>
                </FormAntd>
            </Space>
        </Modal>
    );
};

export default WelfareAddPositionModal;